import type { PersonContactWithAddressEmergencyAccess } from '@quromedical/models';
import { compact, type SyncOrAsyncVoidFn } from '@quromedical/utils';
import { CrudForm, type SubmissionHandler } from 'components/form';
import type { Fetcher } from 'design-system';
import type { FormikErrors } from 'formik';
import { useAddressAutoFill } from 'hooks/useAddressAutoFill';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  initialNextOfKin,
  type JointFields,
  nextOfKinFormSchema,
  resolveFormNextOfKinFields,
} from 'screens/patient';
import { strings } from 'strings';

type ChangeHandler = (
  data: Partial<JointFields>,
  errors: FormikErrors<Partial<JointFields>>,
) => void;

interface NextOfKinFormProps {
  showErrors?: boolean;
  initial?: Partial<JointFields>;
  onChange?: ChangeHandler;
  onSubmit?: SyncOrAsyncVoidFn<Partial<JointFields>>;
  onCancel?: () => void;
  revalidate?: () => void;
  addressFetcher?: Fetcher<string>;
  getAddressPartial?: (
    placeId: string,
  ) => Promise<Partial<PersonContactWithAddressEmergencyAccess>>;
}

export const NextOfKinForm: React.FC<NextOfKinFormProps> = ({
  onChange,
  showErrors,
  onCancel,
  onSubmit,
  addressFetcher,
  getAddressPartial,
  revalidate,
  initial,
}) => {
  const [formState, setFormState] = useState(initialNextOfKin);

  const [initialState, setInitialState] = useState(initial);

  const { onContactChange, placeId } = useAddressAutoFill(
    setFormState,
    getAddressPartial,
  );

  useEffect(() => {
    if (placeId !== initialState?.addressPlaceId) {
      setInitialState(() => ({
        ...initialState,
        ...formState,
        addressPlaceId: placeId,
      }));
    }
  }, [placeId, initialState, formState]);

  const handleChange = useCallback<ChangeHandler>(
    (data, errors) => {
      const resolvedData = {
        ...data,
        addressLines: compact(data.addressLines),
        telecomPhoneNumbers: compact(data.telecomPhoneNumbers),
      };

      onContactChange(resolvedData);
      if (onChange) {
        onChange(resolvedData, errors);
      }
    },
    [onChange, onContactChange],
  );

  const handleSubmit = useCallback<SubmissionHandler<JointFields>>(
    async (data) => {
      try {
        await onSubmit?.(data);
        revalidate?.();
        return {};
      } catch (error) {
        return { error };
      }
    },
    [onSubmit, revalidate],
  );

  const nextOfKinFields = resolveFormNextOfKinFields(formState, addressFetcher);

  const initialValues = initialState || {};
  return (
    <CrudForm
      key={initialState?.addressPlaceId}
      title={strings.FormLabelNextOfKin}
      fields={nextOfKinFields}
      showSubmitButton={!!onSubmit}
      onSubmit={handleSubmit}
      showSecondarySubmitButton={!!onCancel}
      onSecondarySubmit={onCancel}
      initialValues={initialValues}
      includeIcons={true}
      validationSchema={nextOfKinFormSchema}
      onChange={handleChange}
      onChangeDebounceTime={0}
      showErrors={showErrors}
      validateOnMount
    />
  );
};
